import { state, updateCurrentRoute } from '../lib/state.js';
import { calculateRouteForPoints } from '../services/route-service.js';
import { drawRoute, captureMap, showLargeMapPreview, showLargeMapBorderEditMarker, moveLargeMapBorderEditMarker, clearLargeMapBorderEditMarker, beginLargeMapPointPick, cancelLargeMapPointPick } from '../ui/map.js';
import { els } from '../app/dom.js';
import { requestRender } from '../app/events.js';
import { setStatus, esc, finiteNumberOrNull, hasCoords } from '../lib/app-helpers.js';
import {
  pointsForCurrentView, getRoutePlanPoints, normalizeRoutePlanPoints, commitRoutePlan,
  editablePlanFromCalculatedRoute, isRouteConstraint, isManualBorderConstraint,
  parseBorderPairFromText, borderPairFromPoint, normalizeManualBorderName, normalizeRouteConstraintName,
  actualRouteStops, collectRouteConstraints, constraintAnchors, findRouteSegmentAnchors,
  nextConstraintOrder, rebuildRouteWithConstraints, routeCountriesLabel, missingRequestedBorderPairs
} from './route-plan.js';

let editingBorderPoint = null;
let editingBorderMode = 'recalculate';
let cancelBorderMapPick = null;

export function routeForMapPreview() {
  if (state.currentRoute.pointEditMode && state.currentRoute.routeEditPreview?.routeGeometry) {
    return state.currentRoute.routeEditPreview;
  }
  return state.currentRoute;
}

export async function openMapPreview() {
  let previewRoute = routeForMapPreview();
  const routePoints = previewRoute.expandedPoints?.length
    ? previewRoute.expandedPoints
    : (previewRoute.routePlanPoints?.length ? previewRoute.routePlanPoints : previewRoute.originalPoints);

  if (!routePoints?.length || routePoints.length < 2) {
    setStatus(els.importStatus, 'Najpierw wczytaj i wyznacz trasę.', 'warning');
    return;
  }

  const hasRoadGeometry = previewRoute.routeGeometry?.type === 'LineString'
    && previewRoute.routeGeometry.coordinates?.length >= 2;

  if (!hasRoadGeometry) {
    setStatus(els.importStatus, 'Wyznaczam dokładny przebieg drogi do dużego podglądu…', 'working');
    try {
      const metrics = await calculateRouteForPoints(routePoints);
      updateCurrentRoute({
        routeGeometry: metrics.geometry,
        distanceMeters: state.currentRoute.distanceMeters || metrics.distanceMeters,
        durationSeconds: state.currentRoute.durationSeconds || metrics.durationSeconds,
        dirty: true,
      });
      previewRoute = state.currentRoute;
      drawRoute(state.currentRoute);
    } catch (error) {
      setStatus(els.importStatus, `Nie udało się wyznaczyć przebiegu drogi: ${error.message}`, 'error');
      return;
    }
  }

  els.mapPreviewDialog?.classList.remove('hidden');
  els.mapPreviewDialog?.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  await showLargeMapPreview(previewRoute, { onBorderClick: point => openBorderEditor(point, { mode:state.currentRoute.pointEditMode ? 'draft' : 'recalculate' }) });
}

export function closeMapPreview() {
  closeBorderEditor();
  els.mapPreviewDialog?.classList.add('hidden');
  els.mapPreviewDialog?.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}



export function enterPointsEditMode({ silent = false } = {}) {
  const calculated = Boolean(state.currentRoute.expandedPoints?.length && state.currentRoute.routeGeometry);
  const plan = calculated
    ? editablePlanFromCalculatedRoute(state.currentRoute)
    : getRoutePlanPoints(state.currentRoute);
  if (!plan.length) return;

  if (!els.mapPreviewDialog?.classList.contains('hidden')) closeMapPreview();
  const normalized = normalizeRoutePlanPoints(plan);
  const routeEditPreview = calculated ? {
    originalPoints:(state.currentRoute.originalPoints || []).map(point => ({ ...point })),
    expandedPoints:(state.currentRoute.expandedPoints || []).map(point => ({ ...point })),
    routePlanPoints:(state.currentRoute.routePlanPoints || []).map(point => ({ ...point })),
    routeGeometry:state.currentRoute.routeGeometry,
    routeLegs:state.currentRoute.routeLegs || [],
    distanceMeters:state.currentRoute.distanceMeters || 0,
    durationSeconds:state.currentRoute.durationSeconds || 0,
  } : (state.currentRoute.routeEditPreview || null);

  updateCurrentRoute({
    ...normalized,
    routeEditPreview,
    expandedPoints:[],
    sections:[],
    routeGeometry:null,
    routeLegs:[],
    distanceMeters:0,
    durationSeconds:0,
    mapScreenshotDataUrl:'',
    results:{},
    pointEditMode:true,
    dirty:true,
  });
  requestRender();
  if (!silent) {
    const borderCount = normalized.routeConstraints.filter(isManualBorderConstraint).length;
    setStatus(
      els.importStatus,
      `Tryb edycji całego przebiegu jest aktywny. ${borderCount ? `Automatyczne granice (${borderCount}) zostały zamienione na edytowalne punkty wymuszające. ` : ''}Możesz zmieniać ich nazwę i GPS, usuwać granice starego przebiegu albo dodawać nowe. Zmiana samej nazwy nie zmienia drogi — dla przejścia CZ/AT ustaw również GPS tego przejścia lub użyj przycisku mapy. Po zakończeniu kliknij „Wyznacz trasę i granice”.`,
      'warning'
    );
  }
}

export function addEditablePoint(afterPointId = '') {
  if (state.currentRoute.expandedPoints?.length || state.currentRoute.routeGeometry) {
    enterPointsEditMode({ silent:true });
  }
  const plan = getRoutePlanPoints(state.currentRoute);
  const point = {
    id:crypto.randomUUID(),
    name:'Nowy punkt',
    address:'',
    lat:null,
    lon:null,
    sourceType:'manual',
    pointType:'stop',
    isBorder:false,
  };
  let insertIndex = plan.length;
  if (afterPointId) {
    const found = plan.findIndex(item => item.id === afterPointId);
    if (found >= 0) insertIndex = found + 1;
  }
  plan.splice(insertIndex, 0, point);
  commitRoutePlan(plan);
  setStatus(els.importStatus, 'Dodano nowy punkt. Uzupełnij nazwę oraz GPS lub adres, a następnie ponownie wyznacz trasę.', 'warning');
  setTimeout(() => {
    const input = els.pointsBody?.querySelector(`tr[data-point-id="${point.id}"] .name`);
    input?.focus();
    input?.select();
  }, 0);
}

export function addEditableBorderPoint(afterPointId = '') {
  if (state.currentRoute.expandedPoints?.length || state.currentRoute.routeGeometry) {
    enterPointsEditMode({ silent:true });
  }

  const plan = getRoutePlanPoints(state.currentRoute);
  let insertIndex = plan.length;
  if (afterPointId) {
    const found = plan.findIndex(item => item.id === afterPointId);
    if (found >= 0) insertIndex = found + 1;
  }

  const before = plan[Math.max(0, insertIndex - 1)] || null;
  const after = plan[insertIndex] || null;
  const coordinates = midpointCoordinates(before, after);
  const point = {
    id:crypto.randomUUID(),
    name:'Granica XX/YY',
    address:'',
    lat:coordinates?.lat ?? null,
    lon:coordinates?.lon ?? null,
    sourceType:'manual-route-via',
    pointType:'route-via',
    isBorder:false,
    isRouteConstraint:true,
    manualRouteConstraint:true,
    manualBorderConstraint:true,
    requestedBorderPair:'',
    createdAt:new Date().toISOString(),
  };

  plan.splice(insertIndex, 0, point);
  commitRoutePlan(plan);
  setStatus(
    els.importStatus,
    'Dodano edytowalną granicę wymuszającą. Wpisz właściwą parę państw, np. „Granica CZ/AT”, i ustaw GPS przejścia lub wybierz je na mapie.',
    'warning'
  );
  setTimeout(() => {
    const input = els.pointsBody?.querySelector(`tr[data-point-id="${point.id}"] .name`);
    input?.focus();
    input?.select();
  }, 0);
}

export function midpointCoordinates(first, second) {
  const valid = [first, second].filter(hasCoords);
  if (!valid.length) return null;
  if (valid.length === 1) return { lat:Number(valid[0].lat), lon:Number(valid[0].lon) };
  return {
    lat:(Number(valid[0].lat) + Number(valid[1].lat)) / 2,
    lon:(Number(valid[0].lon) + Number(valid[1].lon)) / 2,
  };
}

export function removeEditablePoint(pointId) {
  const plan = getRoutePlanPoints(state.currentRoute);
  const selected = plan.find(point => point.id === pointId);
  if (!selected) return;
  const stopCount = plan.filter(point => !isRouteConstraint(point)).length;
  if (!isRouteConstraint(selected) && stopCount <= 2) {
    setStatus(els.importStatus, 'Trasa musi zawierać co najmniej dwa zwykłe punkty/przystanki.', 'error');
    return;
  }
  commitRoutePlan(plan.filter(point => point.id !== pointId));
  setStatus(els.importStatus, `Usunięto punkt „${selected.name || 'bez nazwy'}”. Ponownie wyznacz trasę.`, 'warning');
}

export function reverseEditableRoute() {
  if (state.currentRoute.expandedPoints?.length || state.currentRoute.routeGeometry) {
    enterPointsEditMode({ silent:true });
  }
  const plan = getRoutePlanPoints(state.currentRoute).reverse();
  if (plan.length < 2) return;
  commitRoutePlan(plan);
  setStatus(els.importStatus, 'Odwrócono kolejność wszystkich punktów. Ponownie wyznacz trasę i granice.', 'warning');
}

export function updateEditablePoint(pointId, patch, { render = false } = {}) {
  const plan = getRoutePlanPoints(state.currentRoute).map(point => point.id === pointId ? { ...point, ...patch } : point);
  commitRoutePlan(plan, { render });
}

export function applyEditableQuery(pointId, value) {
  const text = String(value || '').trim();
  const match = text.match(/^(-?\d+(?:[.,]\d+)?)\s*[,;]\s*(-?\d+(?:[.,]\d+)?)$/);
  if (match) {
    updateEditablePoint(pointId, {
      lat:Number(match[1].replace(',','.')),
      lon:Number(match[2].replace(',','.')),
      address:'',
      countryCode:'',
      countryName:'',
      resolveError:'',
      editedAt:new Date().toISOString(),
    });
  } else {
    updateEditablePoint(pointId, {
      lat:null,
      lon:null,
      address:text,
      countryCode:'',
      countryName:'',
      editedAt:new Date().toISOString(),
    });
  }
  setStatus(els.importStatus, 'Dane punktu zmieniono. Ponownie wyznacz trasę i granice.', 'warning');
}

export function syncEditableRowsToRoutePlan() {
  const rows = [...(els.pointsBody?.querySelectorAll('tr[data-point-id]') || [])];
  if (!rows.length) return getRoutePlanPoints(state.currentRoute);

  const patches = new Map();
  for (const row of rows) {
    const pointId = row.dataset.pointId;
    if (!pointId) continue;
    const nameInput = row.querySelector('input.name:not(:disabled)');
    const queryInput = row.querySelector('input.query:not(:disabled)');
    if (!nameInput && !queryInput) continue;

    const patch = { editedAt:new Date().toISOString() };
    if (nameInput) patch.name = String(nameInput.value || '').trim();

    if (queryInput) {
      const query = String(queryInput.value || '').trim();
      const match = query.match(/^(-?\d+(?:[.,]\d+)?)\s*[,;]\s*(-?\d+(?:[.,]\d+)?)$/);
      if (match) {
        patch.lat = Number(match[1].replace(',', '.'));
        patch.lon = Number(match[2].replace(',', '.'));
        patch.address = '';
        patch.countryCode = '';
        patch.countryName = '';
        patch.resolveError = '';
      } else if (query) {
        patch.lat = null;
        patch.lon = null;
        patch.address = query;
        patch.countryCode = '';
        patch.countryName = '';
      }
    }
    patches.set(pointId, patch);
  }

  const next = getRoutePlanPoints(state.currentRoute).map(point => {
    const patch = patches.get(point.id);
    if (!patch) return point;
    const manualBorder = isManualBorderConstraint(point);
    const enteredName = String(patch.name ?? point.name ?? '').trim();
    const requestedPair = manualBorder
      ? (parseBorderPairFromText(enteredName) || point.requestedBorderPair || '')
      : '';
    return {
      ...point,
      ...patch,
      name:manualBorder
        ? normalizeManualBorderName(enteredName || point.name, requestedPair)
        : (enteredName || point.name || 'Punkt bez nazwy'),
      requestedBorderPair:manualBorder ? requestedPair : point.requestedBorderPair,
    };
  });

  const normalized = commitRoutePlan(next, { render:false });
  return normalized.routePlanPoints;
}

export function openBorderEditor(point, { mode = 'recalculate' } = {}) {
  if ((!point?.isBorder && !isRouteConstraint(point)) || !hasCoords(point)) return;
  editingBorderPoint = { ...point };
  editingBorderMode = mode;
  cancelBorderMapPick?.();
  cancelBorderMapPick = null;

  if (isRouteConstraint(point)) {
    els.borderEditorPair.textContent = 'Edytujesz punkt wymuszający przebieg całej trasy. Możesz przenieść go do innego kraju lub na inne przejście.';
    els.borderEditorName.value = String(point.name || '').trim();
  } else {
    const pair = String(point.countryCode || point.countryName || 'granica').replace('/', ' / ');
    els.borderEditorPair.textContent = `Automatycznie wykryta granica: ${pair}. Wskaż punkt, przez który ma przebiegać cała trasa — może prowadzić przez inne państwa.`;
    els.borderEditorName.value = String(point.name || '').replace(/^Granica\s+/i, '').trim();
  }

  els.borderEditorLat.value = Number(point.lat).toFixed(6);
  els.borderEditorLon.value = Number(point.lon).toFixed(6);
  setBorderEditorStatus(
    editingBorderMode === 'draft'
      ? 'Przeciągnij żółty znacznik albo wskaż na mapie właściwe przejście. Zapiszesz je na liście, a całą trasę przeliczysz później przyciskiem „Wyznacz trasę i granice”.'
      : 'Przeciągnij żółty znacznik albo wskaż na mapie punkt, przez który ma jechać autobus. Cała trasa i wszystkie państwa zostaną wyznaczone od nowa.'
  );
  if (els.saveBorderEditBtn) {
    els.saveBorderEditBtn.textContent = editingBorderMode === 'draft'
      ? 'Zapisz punkt na liście'
      : 'Zapisz i przelicz całą trasę';
  }
  els.borderEditorPanel.classList.remove('hidden');

  showLargeMapBorderEditMarker(point, coordinates => {
    setBorderEditorCoordinates(coordinates);
    setBorderEditorStatus('Punkt przebiegu został zmieniony. Kliknij „Zapisz i przelicz całą trasę”.', 'ok');
  });
}

export function closeBorderEditor() {
  cancelBorderMapPick?.();
  cancelBorderMapPick = null;
  cancelLargeMapPointPick();
  clearLargeMapBorderEditMarker();
  editingBorderPoint = null;
  editingBorderMode = 'recalculate';
  els.borderEditorPanel?.classList.add('hidden');
  if (els.pickBorderPointBtn) els.pickBorderPointBtn.textContent = 'Wskaż punkt przebiegu na mapie';
  if (els.saveBorderEditBtn) els.saveBorderEditBtn.textContent = 'Zapisz i przelicz całą trasę';
}

export function startBorderPointPick() {
  if (!editingBorderPoint) return;
  cancelBorderMapPick?.();
  els.pickBorderPointBtn.textContent = 'Kliknij właściwe miejsce na mapie…';
  setBorderEditorStatus('Kliknij drogę lub przejście, przez które ma przebiegać cała trasa.', 'working');

  cancelBorderMapPick = beginLargeMapPointPick(coordinates => {
    cancelBorderMapPick = null;
    setBorderEditorCoordinates(coordinates);
    els.pickBorderPointBtn.textContent = 'Wskaż punkt przebiegu na mapie';
    setBorderEditorStatus('Nowy punkt przebiegu został wskazany. Możesz go jeszcze poprawić, przeciągając żółty znacznik.', 'ok');
  });
}

export function setBorderEditorCoordinates({ lat, lon }) {
  if (Number.isFinite(Number(lat))) els.borderEditorLat.value = Number(lat).toFixed(6);
  if (Number.isFinite(Number(lon))) els.borderEditorLon.value = Number(lon).toFixed(6);
  moveLargeMapBorderEditMarker({ lat:Number(lat), lon:Number(lon) });
}

export function syncBorderMarkerFromInputs() {
  const lat = finiteNumberOrNull(els.borderEditorLat?.value);
  const lon = finiteNumberOrNull(els.borderEditorLon?.value);
  if (lat === null || lon === null || lat < -90 || lat > 90 || lon < -180 || lon > 180) return;
  moveLargeMapBorderEditMarker({ lat, lon });
}

export async function saveBorderEdit() {
  if (!editingBorderPoint) return;

  const lat = finiteNumberOrNull(els.borderEditorLat?.value);
  const lon = finiteNumberOrNull(els.borderEditorLon?.value);
  const enteredName = String(els.borderEditorName?.value || '').trim();
  if (lat === null || lon === null || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    setBorderEditorStatus('Podaj poprawne współrzędne punktu przebiegu trasy.', 'error');
    return;
  }

  const stops = actualRouteStops(state.currentRoute);
  if (stops.length < 2) {
    setBorderEditorStatus('Brak co najmniej dwóch właściwych przystanków linii.', 'error');
    return;
  }

  const currentConstraints = collectRouteConstraints(state.currentRoute);
  const existingConstraint = isRouteConstraint(editingBorderPoint)
    ? currentConstraints.find(item => item.id === editingBorderPoint.id) || editingBorderPoint
    : null;
  const anchors = existingConstraint
    ? constraintAnchors(existingConstraint, stops)
    : findRouteSegmentAnchors(editingBorderPoint, routeForMapPreview().expandedPoints, stops);

  if (!anchors.afterPointId || !anchors.beforePointId) {
    setBorderEditorStatus('Nie udało się ustalić odcinka linii, którego dotyczy ten punkt.', 'error');
    return;
  }

  const requestedPair = parseBorderPairFromText(enteredName || editingBorderPoint.name)
    || existingConstraint?.requestedBorderPair
    || borderPairFromPoint(editingBorderPoint);
  const manualBorder = isManualBorderConstraint(editingBorderPoint)
    || Boolean(requestedPair)
    || /^granica\b/i.test(enteredName);
  const name = manualBorder
    ? normalizeManualBorderName(enteredName || editingBorderPoint.name, requestedPair)
    : normalizeRouteConstraintName(enteredName || editingBorderPoint.name);
  const constraint = {
    ...(existingConstraint || {}),
    id: existingConstraint?.id || crypto.randomUUID(),
    name,
    address:'',
    lat,
    lon,
    countryCode:'',
    countryName:'',
    sourceType:'manual-route-via',
    pointType:'route-via',
    isBorder:false,
    isRouteConstraint:true,
    manualRouteConstraint:true,
    manualBorderConstraint:manualBorder,
    requestedBorderPair:requestedPair,
    insertAfterPointId:anchors.afterPointId,
    insertBeforePointId:anchors.beforePointId,
    insertAfterIndex:anchors.afterIndex,
    insertBeforeIndex:anchors.beforeIndex,
    segmentOrder: existingConstraint?.segmentOrder ?? nextConstraintOrder(
      currentConstraints,
      anchors.afterPointId,
      anchors.beforePointId,
      editingBorderPoint,
      routeForMapPreview().expandedPoints || []
    ),
    createdAt:existingConstraint?.createdAt || new Date().toISOString(),
    editedAt:new Date().toISOString(),
    sourceBorder: existingConstraint?.sourceBorder || {
      id:editingBorderPoint.id || '',
      name:editingBorderPoint.name || '',
      countryCode:editingBorderPoint.countryCode || '',
      lat:editingBorderPoint.lat,
      lon:editingBorderPoint.lon,
    },
  };

  if (editingBorderMode === 'draft') {
    const plan = getRoutePlanPoints(state.currentRoute);
    const found = plan.some(item => item.id === constraint.id);
    const nextPlan = found
      ? plan.map(item => item.id === constraint.id ? constraint : item)
      : [...plan, constraint];
    commitRoutePlan(nextPlan);
    closeBorderEditor();
    closeMapPreview();
    setStatus(
      els.importStatus,
      `Zapisano punkt „${name}” na liście. ${requestedPair ? `Wymagane przejście: ${requestedPair}. ` : ''}Kliknij „Wyznacz trasę i granice”, aby od początku przeliczyć drogę, państwa i kalkulatory.`,
      'warning'
    );
    return;
  }

  const constraints = existingConstraint
    ? currentConstraints.map(item => item.id === existingConstraint.id ? constraint : item)
    : [...currentConstraints, constraint];

  const button = els.saveBorderEditBtn;
  const previousText = button.textContent;
  button.disabled = true;
  button.textContent = 'Przeliczam całość…';
  setBorderEditorStatus('Wyznaczam od początku cały przebieg, państwa i wszystkie granice…', 'working');

  try {
    const rebuilt = await rebuildRouteWithConstraints(
      stops,
      constraints,
      message => setBorderEditorStatus(message, 'working')
    );

    updateCurrentRoute({
      originalPoints:rebuilt.originalPoints,
      routeConstraints:rebuilt.routeConstraints,
      routePlanPoints:rebuilt.routePlanPoints,
      routeEditPreview:null,
      pointEditMode:false,
      expandedPoints:rebuilt.expandedPoints,
      sections:rebuilt.sections,
      routeGeometry:rebuilt.geometry,
      routeLegs:rebuilt.legs,
      distanceMeters:rebuilt.distanceMeters,
      durationSeconds:rebuilt.durationSeconds,
      mapScreenshotDataUrl:'',
      results:{},
      dirty:true,
    });

    drawRoute(state.currentRoute);
    await showLargeMapPreview(state.currentRoute, { onBorderClick: point => openBorderEditor(point, { mode:'recalculate' }) });
    await new Promise(resolve => setTimeout(resolve, 180));
    updateCurrentRoute({ mapScreenshotDataUrl:captureMap() || '' });
    requestRender();
    closeBorderEditor();

    const countries = routeCountriesLabel(rebuilt.sections);
    const missingPairs = missingRequestedBorderPairs(rebuilt.routePlanPoints, rebuilt.expandedPoints);
    setStatus(
      els.importStatus,
      missingPairs.length
        ? `Trasa została przeliczona, ale nie przechodzi przez wymagane granice: ${missingPairs.join(', ')}. Zmień GPS tych punktów i przelicz ponownie.`
        : `Punkt „${name}” wymusza nowy przebieg całej trasy${countries ? `: ${countries}` : ''}. Wszystkie granice, odcinki i dystanse przeliczono od początku. Poprzednie wyniki kalkulatorów usunięto — uruchom je ponownie.`,
      missingPairs.length ? 'warning' : 'ok'
    );
  } catch (error) {
    console.error('[D24 ROUTE CORRIDOR EDIT]', error);
    setBorderEditorStatus(`Nie udało się wyznaczyć całej trasy przez ten punkt: ${error.message}`, 'error');
  } finally {
    button.disabled = false;
    button.textContent = previousText;
  }
}

export function setBorderEditorStatus(message, kind = '') {
  if (!els.borderEditorStatus) return;
  els.borderEditorStatus.textContent = message;
  els.borderEditorStatus.className = `border-editor-status${kind ? ` ${kind}` : ''}`;
}


export function renderPoints(){
  els.pointsBody.textContent='';
  const calculated = Boolean(state.currentRoute.expandedPoints?.length);
  const points = calculated ? state.currentRoute.expandedPoints : getRoutePlanPoints(state.currentRoute);

  points.forEach((point,index)=>{
    const tr=document.createElement('tr');
    tr.dataset.pointId=point.id || '';
    const routeConstraint=isRouteConstraint(point);
    const manualBorder=isManualBorderConstraint(point);
    const editable=!calculated && !point.isBorder;
    const typeClass=point.isBorder?'border':manualBorder?'manual-border':routeConstraint?'route-via':'';
    const typeLabel=point.isBorder?'Granica':manualBorder?'Granica wymuszona':routeConstraint?'Wymuszony przebieg':'Przystanek';
    const pair = borderPairFromPoint(point);
    const country = calculated
      ? (point.countryName||point.countryCode||'—')
      : (manualBorder ? (pair || 'ustaw parę i GPS') : (point.countryName||point.countryCode||'do przeliczenia'));
    const queryValue = hasCoords(point)
      ? `${Number(point.lat).toFixed(7)}, ${Number(point.lon).toFixed(7)}`
      : (point.address||'');

    let actions = '';
    if (editable) {
      const mapButton = routeConstraint && hasCoords(point)
        ? '<button class="point-row-button edit-border-on-map" type="button" title="Wskaż ten punkt na dużej mapie">⌖</button>'
        : '';
      actions = `<div class="point-actions">
        <button class="point-row-button add-after-point" type="button" title="Dodaj przystanek po tej pozycji">＋</button>
        <button class="point-row-button add-border-after-point" type="button" title="Dodaj granicę wymuszającą po tej pozycji">G＋</button>
        ${mapButton}
        <button class="point-row-button remove-point" type="button" title="Usuń punkt">×</button>
      </div>`;
    } else if (point.isBorder) {
      actions = '<div class="point-actions"><button class="point-row-button edit-calculated-border" type="button" title="Przejdź do edycji tej granicy">✎</button></div>';
    }

    if (manualBorder) tr.classList.add('editable-border-row');
    tr.innerHTML=`<td>${index+1}</td><td><input class="name" ${editable?'':'disabled'} value="${esc(point.name)}"></td><td><input class="query" ${editable?'':'disabled'} value="${esc(queryValue)}"></td><td>${esc(country)}</td><td><span class="point-type ${typeClass}">${typeLabel}</span></td><td>${actions}</td>`;

    if(editable){
      const nameInput=tr.querySelector('.name');
      const persistName=({ showStatus=false }={})=>{
        const rawValue=String(nameInput.value||'');
        const value=rawValue.trim()||(manualBorder?'Granica wymuszona':'Punkt bez nazwy');
        const requestedPair=manualBorder ? (parseBorderPairFromText(value)||point.requestedBorderPair||'') : '';
        updateEditablePoint(point.id,{
          name:manualBorder ? normalizeManualBorderName(value,requestedPair) : value,
          requestedBorderPair:requestedPair,
          manualBorderConstraint:manualBorder,
          editedAt:new Date().toISOString(),
        });
        if(showStatus){
          setStatus(
            els.importStatus,
            manualBorder
              ? `Zmieniono nazwę granicy${requestedPair?` na ${requestedPair}`:''}. Pamiętaj: sama nazwa nie zmienia przebiegu — ustaw też GPS właściwego przejścia lub użyj przycisku ⌖.`
              : 'Nazwę punktu zmieniono. Ponownie wyznacz trasę i granice.',
            'warning'
          );
        }
      };
      nameInput.addEventListener('input',()=>persistName());
      nameInput.addEventListener('change',()=>persistName({showStatus:true}));
      tr.querySelector('.query').addEventListener('change',event=>applyEditableQuery(point.id,event.target.value));
      tr.querySelector('.add-after-point').addEventListener('click',()=>addEditablePoint(point.id));
      tr.querySelector('.add-border-after-point').addEventListener('click',()=>addEditableBorderPoint(point.id));
      tr.querySelector('.edit-border-on-map')?.addEventListener('click',()=>openConstraintEditorFromList(point));
      tr.querySelector('.remove-point').addEventListener('click',()=>removeEditablePoint(point.id));
    } else if (point.isBorder) {
      tr.querySelector('.edit-calculated-border')?.addEventListener('click',()=>startCalculatedBorderEdit(point));
    }
    els.pointsBody.appendChild(tr);
  });

  const borders=points.filter(point=>point.isBorder).length;
  const constraints=points.filter(isRouteConstraint).length;
  const editableBorders=points.filter(isManualBorderConstraint).length;
  const mode = calculated ? '' : ' Tryb edycji jest aktywny.';
  els.pointsSummary.textContent=`${points.length} punktów${borders?`, w tym ${borders} automatycznych granic`:''}${constraints?` i ${constraints} wymuszających przebieg`:''}${editableBorders?` (${editableBorders} edytowalnych granic)`:''}.${mode}`;
}

export async function startCalculatedBorderEdit(borderPoint) {
  enterPointsEditMode({ silent:true });
  const plan = getRoutePlanPoints(state.currentRoute);
  const converted = plan.find(point => (
    isManualBorderConstraint(point)
    && (
      point.sourceBorder?.id === borderPoint.id
      || (
        Math.abs(Number(point.lat) - Number(borderPoint.lat)) < 0.00001
        && Math.abs(Number(point.lon) - Number(borderPoint.lon)) < 0.00001
      )
    )
  ));
  requestRender();
  setStatus(
    els.importStatus,
    'Granice zostały przeniesione do edycji. Zmień nazwę i GPS, usuń granice niepotrzebnego kraju albo dodaj nowe, a następnie kliknij „Wyznacz trasę i granice”.',
    'warning'
  );
  if (converted) await openConstraintEditorFromList(converted);
}

export async function openConstraintEditorFromList(point) {
  const previewRoute = routeForMapPreview();
  if (!previewRoute?.routeGeometry || !hasCoords(point)) {
    setStatus(els.importStatus, 'Najpierw podaj poprawne GPS granicy, a potem otwórz ją na mapie.', 'warning');
    return;
  }

  els.mapPreviewDialog?.classList.remove('hidden');
  els.mapPreviewDialog?.setAttribute('aria-hidden','false');
  document.body.style.overflow='hidden';
  await showLargeMapPreview(previewRoute, { onBorderClick: clicked => openBorderEditor(clicked, { mode:'draft' }) });
  openBorderEditor(point, { mode:'draft' });
}

